/**
 * Optional support links on the Atlas sign-in card, mirroring Atlas Desktop
 * (Provider Portal) — "Forgot password?" beside the Password label and
 * "Need login assistance?" under the Login button.
 *
 * Both are OPT-IN per environment and render only when their env var holds a
 * usable URL. Atlas Desktop's own destinations are not defined in this repo,
 * so shipping them unconditionally would mean shipping links that go nowhere;
 * an env var lets each deployment point them at the right place (and lets
 * prod turn them on) without a code change.
 *
 *   VITE_ATLAS_FORGOT_PASSWORD_URL   (unset → link hidden)
 *   VITE_ATLAS_LOGIN_HELP_URL        (unset → link hidden)
 *
 * Only http(s) URLs are accepted. Anything else — including `javascript:`, a
 * relative path, or a typo — is dropped with a console.warn rather than
 * rendered, so a bad env value can never turn into a live link on the one
 * page we show to unauthenticated users.
 */

function readHttpUrl(raw: string | undefined, envName: string): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // eslint-disable-next-line no-console
    console.warn(`[login] ${envName} is not a valid URL — link hidden.`);
    return null;
  }
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    // eslint-disable-next-line no-console
    console.warn(
      `[login] ${envName} must be an http(s) URL (got "${parsed.protocol}") — link hidden.`,
    );
    return null;
  }
  return parsed.toString();
}

// Read as static property accesses: Vite only statically replaces the literal
// `import.meta.env.VITE_FOO` form at build time (see authProviders.ts).
export const forgotPasswordUrl = readHttpUrl(
  import.meta.env.VITE_ATLAS_FORGOT_PASSWORD_URL,
  "VITE_ATLAS_FORGOT_PASSWORD_URL",
);

export const loginHelpUrl = readHttpUrl(
  import.meta.env.VITE_ATLAS_LOGIN_HELP_URL,
  "VITE_ATLAS_LOGIN_HELP_URL",
);
